// Top-of-screen tutorial hint banner.
//
// Renders a context-aware control hint (walk → sprint → climb → combat →
// throw → follow) for the duration of the tutorial. Updates on phase
// transitions, sub-step changes (e.g. NPC dropped into pit), and input-
// modality changes (kbm / gamepad / touch). Hints are gated on the
// matching narration line having finished, so the prompt fades in only
// after the narrator has actually told the player what to do. A second
// always-on "small" banner shows the skip-tutorial control.
//
// Lifecycle is owned by runTutorial(): construct after the canvas mount,
// call tick() once per render frame, dispose() on tutorial teardown.

import { t, onLocaleChange } from '../i18n';
import type { Input, InputModality } from '../core/Input';
import type { TutorialFlowBehavior } from '../maps/bundles/tutorial/behaviors/tutorialFlow';
import type { NarratorCoordinator } from '../maps/bundles/tutorial/narration/NarratorCoordinator';
import {
  ensureControlHintStyles,
  gamepadGlyph,
  isControlHeld,
  labelFor,
  sampleInputState,
  type ControlName
} from './controlHintShared';

interface BannerDeps {
  input: Input;
  flow: TutorialFlowBehavior;
  coord: NarratorCoordinator;
  /** Render-frame read of whether the local player is currently
   *  double-handing a victim (so phase-6 hint flips between throw and
   *  follow-in). */
  isDoubleHolding: () => boolean;
}

type HintKind =
  | 'walk' | 'sprint' | 'sprintJump' | 'climb'
  | 'combat'
  | 'throw' | 'followIn'
  | 'complete';

interface HintRow {
  key: string;
  vars: Record<string, ControlName>;
  doneKind?: 'punch' | 'headbutt' | 'jumpkick';
}

interface HintBlock {
  titleKey?: string;
  rows: HintRow[];
}

const FADE_MS = 280;

// Each hint kind unlocks only after its corresponding narration line has
// finished. `null` means no prerequisite (e.g. the closing 'complete' state
// can show as soon as we arrive there). Mirrors the keys NarratorCoordinator
// records into finishedKeys.
const PREREQUISITE: Record<HintKind, string | null> = {
  walk: '1_2_awake',
  sprint: '2_1_sprint',
  sprintJump: '3_1_run_jump',
  climb: '4_1_wall_climb',
  combat: '5_1_jimmy_combat',
  throw: '6_1_throw_jimmy',
  followIn: '6_2_follow_jimmy',
  complete: null
};

type FadeState = 'hidden' | 'fading-in' | 'visible' | 'fading-out';

export class TutorialHintBanner {
  private deps: BannerDeps;
  private rootMain: HTMLElement;   // primary banner (fades in/out per hint)
  private rootSkip: HTMLElement;   // small always-on skip banner
  private cardMain: HTMLElement;
  private cardSkip: HTMLElement;
  private modality: InputModality;
  private detachLocale: (() => void) | null = null;

  // Fade state machine for the main banner.
  private active: HintKind | null = null;
  private fadeState: FadeState = 'hidden';
  private fadeUntil = 0;

  // Last rendered modality for both panels — re-renders on change.
  private lastMainModality: InputModality | null = null;
  private lastSkipModality: InputModality | null = null;

  // Per-frame keyboard / mouse state — banner owns its own listeners so it
  // can read held state without piping anything new through the Input class.
  private keys = new Set<string>();
  private mouseL = false;
  private mouseR = false;

  // DOM nodes we update each frame without re-rendering.
  private keyEls: { name: ControlName; el: HTMLElement }[] = [];
  private checkEls: Partial<Record<'punch' | 'headbutt' | 'jumpkick', HTMLElement>> = {};

  private onKeyDown = (e: KeyboardEvent): void => { this.keys.add(e.code); };
  private onKeyUp = (e: KeyboardEvent): void => { this.keys.delete(e.code); };
  private onMouseDown = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseL = true;
    if (e.button === 2) this.mouseR = true;
  };
  private onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.mouseL = false;
    if (e.button === 2) this.mouseR = false;
  };

  constructor(deps: BannerDeps) {
    this.deps = deps;
    this.modality = deps.input.getModality();

    ensureControlHintStyles();

    this.rootMain = document.createElement('div');
    this.rootMain.id = 'tutorial-hint-banner';
    this.rootMain.className = 'thb';
    this.cardMain = document.createElement('div');
    this.cardMain.className = 'thb-card';
    this.cardMain.style.opacity = '0';
    this.rootMain.appendChild(this.cardMain);
    document.body.appendChild(this.rootMain);

    this.rootSkip = document.createElement('div');
    this.rootSkip.id = 'tutorial-hint-skip';
    this.rootSkip.className = 'thb thb-skip-pos';
    this.cardSkip = document.createElement('div');
    this.cardSkip.className = 'thb-card thb-card-small';
    this.cardSkip.style.opacity = '0';
    this.rootSkip.appendChild(this.cardSkip);
    document.body.appendChild(this.rootSkip);

    addEventListener('keydown', this.onKeyDown);
    addEventListener('keyup', this.onKeyUp);
    addEventListener('mousedown', this.onMouseDown);
    addEventListener('mouseup', this.onMouseUp);

    this.detachLocale = onLocaleChange(() => {
      // Force re-render on locale change.
      this.lastMainModality = null;
      this.lastSkipModality = null;
      if (this.active) this.renderMain(this.active);
    });
  }

  dispose(): void {
    removeEventListener('keydown', this.onKeyDown);
    removeEventListener('keyup', this.onKeyUp);
    removeEventListener('mousedown', this.onMouseDown);
    removeEventListener('mouseup', this.onMouseUp);
    this.detachLocale?.();
    this.detachLocale = null;
    this.rootMain.remove();
    this.rootSkip.remove();
  }

  /** Called once per render frame from runTutorial's render callback. */
  tick(): void {
    const now = performance.now();
    this.modality = this.deps.input.getModality();
    const desired = this.computeDesired();

    // Skip banner: fades in once at boot and stays visible. Re-renders
    // when modality changes so the keycap reflects the right control.
    if (this.modality !== this.lastSkipModality) {
      this.renderSkip();
      this.lastSkipModality = this.modality;
      // Trigger CSS transition by pushing opacity to 1 next frame.
      requestAnimationFrame(() => { this.cardSkip.style.opacity = '1'; });
    }

    // Main banner state machine.
    switch (this.fadeState) {
      case 'hidden':
        if (desired !== null) {
          this.active = desired;
          this.renderMain(desired);
          this.lastMainModality = this.modality;
          this.cardMain.style.opacity = '0';
          // requestAnimationFrame ensures the browser commits opacity:0
          // before transitioning to 1, so the fade-in actually animates.
          requestAnimationFrame(() => {
            this.cardMain.style.opacity = '1';
          });
          this.fadeState = 'fading-in';
          this.fadeUntil = now + FADE_MS;
        }
        break;

      case 'fading-in':
        if (now >= this.fadeUntil) this.fadeState = 'visible';
        break;

      case 'visible':
        if (desired !== this.active || this.modality !== this.lastMainModality) {
          this.cardMain.style.opacity = '0';
          this.fadeState = 'fading-out';
          this.fadeUntil = now + FADE_MS;
        }
        break;

      case 'fading-out':
        if (now >= this.fadeUntil) {
          if (desired === null) {
            this.active = null;
            this.fadeState = 'hidden';
          } else {
            this.active = desired;
            this.renderMain(desired);
            this.lastMainModality = this.modality;
            requestAnimationFrame(() => {
              this.cardMain.style.opacity = '1';
            });
            this.fadeState = 'fading-in';
            this.fadeUntil = now + FADE_MS;
          }
        }
        break;
    }

    this.refreshHeld();
    this.refreshChecks();
  }

  // --- hint state derivation ---------------------------------------------

  private computeDesired(): HintKind | null {
    const phase = this.deps.flow.phase;
    let kind: HintKind | null = null;
    if (phase === 'complete' || phase === 'skipped') kind = null;
    else if (phase === 1) kind = 'walk';
    else if (phase === 2) kind = 'sprint';
    else if (phase === 3) kind = 'sprintJump';
    else if (phase === 4) kind = 'climb';
    else if (phase === 5) kind = 'combat';
    else if (phase === 6) kind = this.deps.flow.npcDroppedIn ? 'followIn' : 'throw';

    if (kind === null) return null;

    // Gate on matching narration finishing — keeps the prompt out of view
    // while the narrator is still mid-sentence about the action.
    const prereq = PREREQUISITE[kind];
    if (prereq && !this.deps.coord.hasFinished(prereq)) return null;
    return kind;
  }

  private blockFor(kind: HintKind): HintBlock {
    switch (kind) {
      case 'walk':
        return { rows: [{ key: 'tutorial.hint.walk', vars: { keys: 'move' } }] };
      case 'sprint':
        return { rows: [{ key: 'tutorial.hint.sprint', vars: { sprint: 'sprint', move: 'move' } }] };
      case 'sprintJump':
        return { rows: [{ key: 'tutorial.hint.sprintJump', vars: { sprint: 'sprint', move: 'move', jump: 'jump' } }] };
      case 'climb':
        return { rows: [{ key: 'tutorial.hint.climb', vars: { grab: 'grab', move: 'move', jump: 'jump' } }] };
      case 'combat':
        return {
          titleKey: 'tutorial.hint.combat.title',
          rows: [
            { key: 'tutorial.hint.combat.punch',    vars: { key: 'punch' },    doneKind: 'punch' },
            { key: 'tutorial.hint.combat.headbutt', vars: { key: 'headbutt' }, doneKind: 'headbutt' },
            { key: 'tutorial.hint.combat.jumpkick', vars: { key: 'kick' },     doneKind: 'jumpkick' }
          ]
        };
      case 'throw':
        return { rows: [{ key: 'tutorial.hint.throw', vars: { grab: 'grab', throw: 'throw' } }] };
      case 'followIn':
        return { rows: [{ key: 'tutorial.hint.followIn', vars: {} }] };
      case 'complete':
        return { rows: [{ key: 'tutorial.hint.complete', vars: {} }] };
    }
  }

  // --- rendering ---------------------------------------------------------

  private renderMain(kind: HintKind): void {
    this.keyEls = [];
    this.checkEls = {};

    const block = this.blockFor(kind);
    const isCombat = kind === 'combat';
    this.cardMain.classList.toggle('thb-stack', isCombat);

    const inner = document.createDocumentFragment();
    if (block.titleKey) {
      const title = document.createElement('div');
      title.className = 'thb-title';
      title.textContent = t(block.titleKey);
      inner.appendChild(title);
    }
    for (const row of block.rows) {
      const rowEl = document.createElement('div');
      rowEl.className = 'thb-row';
      this.renderRow(rowEl, row);
      inner.appendChild(rowEl);
    }
    this.cardMain.replaceChildren(inner);
  }

  private renderSkip(): void {
    const row = document.createElement('div');
    row.className = 'thb-row';
    this.renderRow(row, { key: 'tutorial.hint.skip', vars: { key: 'menu' } });
    this.cardSkip.replaceChildren(row);
  }

  private renderRow(parent: HTMLElement, row: HintRow): void {
    const template = t(row.key);
    const parts = template.split(/(\{[a-zA-Z]+\})/g);
    const body = document.createElement('span');
    body.className = 'thb-row-body';

    for (const part of parts) {
      const m = /^\{([a-zA-Z]+)\}$/.exec(part);
      if (m) {
        const placeholder = m[1]!;
        const ctrl = row.vars[placeholder];
        if (ctrl) {
          body.appendChild(this.renderKeycap(ctrl));
        } else {
          body.appendChild(document.createTextNode(part));
        }
      } else if (part.length > 0) {
        body.appendChild(document.createTextNode(part));
      }
    }

    parent.appendChild(body);

    if (row.doneKind) {
      const check = document.createElement('span');
      check.className = 'thb-check';
      check.textContent = '✓';
      this.checkEls[row.doneKind] = check;
      parent.appendChild(check);
    }
  }

  private renderKeycap(name: ControlName): HTMLElement {
    const cap = document.createElement('span');
    cap.className = 'thb-key';
    cap.dataset.control = name;

    if (this.modality === 'gamepad') {
      cap.classList.add('thb-key-gp');
      cap.innerHTML = gamepadGlyph(name);
    } else {
      cap.textContent = labelFor(name, this.modality);
    }

    this.keyEls.push({ name, el: cap });
    return cap;
  }

  // --- per-frame highlight refresh --------------------------------------

  private refreshHeld(): void {
    if (this.keyEls.length === 0) return;
    const sample = sampleInputState(this.modality, this.keys, this.mouseL, this.mouseR);
    for (const { name, el } of this.keyEls) {
      const held = isControlHeld(name, this.modality, sample);
      el.classList.toggle('held', held);
    }
  }

  private refreshChecks(): void {
    const hits = this.deps.flow.room5Hits;
    if (this.checkEls.punch)    this.checkEls.punch.classList.toggle('on',    hits.punch);
    if (this.checkEls.headbutt) this.checkEls.headbutt.classList.toggle('on', hits.headbutt);
    if (this.checkEls.jumpkick) this.checkEls.jumpkick.classList.toggle('on', hits.jumpkick);
  }
}
